import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import axios from 'axios';
import { X, CheckCircle2 } from 'lucide-react';
import { API } from '../content/data';
import { mediaUrl } from './bits';
import { EASE } from './motion';

const seen = (id) => sessionStorage.getItem(`ditec_popup_${id}`);
const markSeen = (id) => sessionStorage.setItem(`ditec_popup_${id}`, '1');

function FormPopup({ popup, onClose }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [status, setStatus] = useState('idle');
  const inputCls = 'w-full rounded-xl border border-navy/15 bg-white px-4 py-3.5 text-sm text-navy outline-none transition-colors focus:border-electric placeholder:text-navy/40';
  const submit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await axios.post(`${API}/leads`, { ...form, interest: popup.title.slice(0, 60) });
      setStatus('done');
    } catch {
      setStatus('idle');
    }
  };
  return (
    <div className="p-6 sm:p-8">
      {status === 'done' ? (
        <div data-testid="popup-form-success" className="py-6 text-center">
          <CheckCircle2 className="mx-auto h-11 w-11 text-electric" />
          <h3 className="text-h3 mt-4 font-semibold text-navy">Thank you.</h3>
          <p className="mt-2 text-sm text-navy/65">The DITEC team will reach out shortly.</p>
        </div>
      ) : (
        <>
          <h3 className="text-h3 pr-8 font-semibold text-navy">{popup.title}</h3>
          {popup.body && <p className="mt-2 text-sm leading-relaxed text-navy/65">{popup.body}</p>}
          <form onSubmit={submit} data-testid="popup-form" className="mt-5 space-y-3">
            <input data-testid="popup-form-name" required placeholder="Full Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
            <input data-testid="popup-form-phone" required type="tel" placeholder="Phone Number" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className={inputCls} />
            <input data-testid="popup-form-email" required type="email" placeholder="Email Address" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} />
            <button data-testid="popup-form-submit" type="submit" disabled={status === 'loading'} className="w-full touch-target rounded-full bg-electric py-3.5 text-sm font-semibold text-white transition-colors hover:bg-navy disabled:opacity-60">
              {status === 'loading' ? 'Sending…' : popup.ctaLabel || 'Submit'}
            </button>
          </form>
        </>
      )}
    </div>
  );
}

function PopupCard({ popup, onClose }) {
  const closeBtn = (
    <button data-testid="popup-close" onClick={onClose} aria-label="Close popup" className="absolute right-3 top-3 z-10 flex touch-target items-center justify-center rounded-full bg-black/10 text-navy/60 backdrop-blur transition-colors hover:bg-black/20 hover:text-navy">
      <X className="h-4 w-4" />
    </button>
  );

  if (popup.type === 'notification') {
    return (
      <motion.div
        data-testid="popup-notification"
        initial={{ opacity: 0, y: 40, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40, scale: 0.96 }}
        transition={{ duration: 0.45, ease: EASE }}
        className="fixed bottom-24 right-4 z-[85] w-[calc(100vw-2rem)] max-w-sm rounded-3xl border border-navy/10 bg-white p-6 shadow-2xl shadow-navy/20 sm:bottom-6 sm:right-6"
      >
        {closeBtn}
        <span className="overline !text-[10px] text-electric">Announcement</span>
        <h3 className="mt-2 pr-8 text-lg font-semibold tracking-tight text-navy">{popup.title}</h3>
        {popup.body && <p className="mt-2 text-sm leading-relaxed text-navy/65">{popup.body}</p>}
        {popup.ctaLabel && popup.ctaUrl && (
          <a data-testid="popup-cta" href={popup.ctaUrl} className="mt-4 inline-flex touch-target items-center rounded-full bg-electric px-6 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-navy">
            {popup.ctaLabel} →
          </a>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      data-testid={`popup-${popup.type}`}
      className="fixed inset-0 z-[85] flex items-end justify-center p-0 sm:items-center sm:p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-navy/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ y: 60, scale: 0.97 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 60, scale: 0.97 }}
        transition={{ duration: 0.45, ease: EASE }}
        className="relative max-h-[90vh] w-full overflow-y-auto rounded-t-3xl bg-white shadow-2xl sm:max-w-md sm:rounded-3xl"
      >
        {closeBtn}
        {popup.type === 'image' && (
          <div>
            {popup.image && <img src={mediaUrl(popup.image)} alt={popup.title} className="w-full rounded-t-3xl object-cover sm:rounded-t-3xl" />}
            <div className="p-6 sm:p-7">
              <h3 className="text-h3 font-semibold text-navy">{popup.title}</h3>
              {popup.body && <p className="mt-2 text-sm leading-relaxed text-navy/65">{popup.body}</p>}
              {popup.ctaLabel && popup.ctaUrl && (
                <a data-testid="popup-cta" href={popup.ctaUrl} className="mt-5 inline-flex touch-target items-center rounded-full bg-electric px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy">
                  {popup.ctaLabel} →
                </a>
              )}
            </div>
          </div>
        )}
        {popup.type === 'form' && <FormPopup popup={popup} onClose={onClose} />}
      </motion.div>
    </motion.div>
  );
}

export default function PopupManager() {
  const { pathname } = useLocation();
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/popups`).then((r) => setQueue((r.data || []).filter((p) => !seen(p.id)))).catch(() => {});
  }, []);

  useEffect(() => {
    setCurrent(null);
    if (!queue.length) return;
    const next = queue[0];
    timerRef.current = setTimeout(() => setCurrent(next), (next.delaySeconds ?? 3) * 1000);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, queue]);

  const close = () => {
    if (current) markSeen(current.id);
    const rest = queue.slice(1);
    setCurrent(null);
    setQueue(rest);
  };

  return (
    <AnimatePresence>
      {current && <PopupCard key={current.id} popup={current} onClose={close} />}
    </AnimatePresence>
  );
}
